import { useState } from "react";
import { useNavigate, Link, useLocation, Navigate } from "react-router-dom";
import { BackButton } from "../lib/nav";
import { useAuth, homeForRole } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Logo } from "../components/Brand";
import { toast } from "sonner";

export default function Login() {
  const { login, verifyMfa, user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = (u) => (loc.state?.from && !/^\/(login|register|forgot-password|reset-password)/.test(String(loc.state.from)) ? loc.state.from : homeForRole(u.role));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      if (u.mfa_required) { setMfa(u.mfa_token); setBusy(false); return; }
      toast.success(`Welcome back, ${u.name}`); nav(dest(u), { replace: true });
    }
    catch (ex) { setErr(formatApiError(ex.response?.data?.detail) || ex.message); }
    setBusy(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const u = await verifyMfa(mfa, code); toast.success(`Welcome back, ${u.name}`); nav(dest(u), { replace: true }); }
    catch (ex) { setErr(formatApiError(ex.response?.data?.detail) || ex.message); if (ex.response?.status === 401 && /expired/i.test(ex.response?.data?.detail || "")) setMfa(null); }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (loc.state?.from) sessionStorage.setItem("rav3_after_login", loc.state.from);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) return null;
  if (user && !mfa) return <Navigate to={dest(user)} replace />;

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 bg-[#0A0A0C]">
      <div className="w-full max-w-md fade-up">
        <div className="flex justify-center mb-8"><Logo size={56} /></div>
        <div className="card-copper rounded-2xl p-8">
          <BackButton fallback="/" className="mb-4" />
          <h1 className="font-display text-2xl font-bold uppercase">{mfa ? "Two-Factor Code" : "Sign In"}</h1>
          <p className="mono-label text-[#71717A] mt-1">{mfa ? "ENTER THE 6-DIGIT CODE FROM YOUR AUTHENTICATOR APP OR A RECOVERY CODE" : "CONTROL ROOM ACCESS"}</p>
          {mfa ? (
            <form onSubmit={submitMfa} className="space-y-4 mt-6">
              <input data-testid="mfa-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" className="w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-3 text-sm outline-none focus:border-[#E8552A] font-mono tracking-widest" />
              {err && <p data-testid="login-error" className="text-sm text-[#ef4444]">{err}</p>}
              <button data-testid="mfa-submit" disabled={busy} className="w-full copper-btn rounded-lg py-3 mono-label disabled:opacity-60">{busy ? "VERIFYING…" : "VERIFY"}</button>
              <button type="button" onClick={() => { setMfa(null); setCode(""); setErr(""); }} className="w-full mono-label text-[#71717A] hover:text-[#E8552A]">BACK</button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-3 text-sm outline-none focus:border-[#E8552A]" />
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-[#0A0A0C] border border-[#27272A] rounded-lg px-4 py-3 text-sm outline-none focus:border-[#E8552A]" />
            {err && <p data-testid="login-error" className="text-sm text-[#ef4444]">{err}</p>}
            <button data-testid="login-submit" disabled={busy} className="w-full copper-btn rounded-lg py-3 mono-label disabled:opacity-60">{busy ? "SIGNING IN…" : "SIGN IN"}</button>
          </form>
          )}
          {!mfa && <>
          <div className="flex items-center gap-3 my-5"><div className="h-px bg-[#27272A] flex-1" /><span className="mono-label text-[#71717A]">OR</span><div className="h-px bg-[#27272A] flex-1" /></div>
          <button data-testid="google-login" onClick={google} className="w-full border border-[#27272A] rounded-lg py-3 mono-label text-[#F4F4F5] hover:border-[#E8552A]">CONTINUE WITH GOOGLE</button>
          <p className="text-sm text-[#71717A] text-center mt-6">New here? <Link to="/register" className="text-[#E8552A] hover:underline">Create an account</Link> · <Link data-testid="forgot-link" to="/forgot-password" className="text-[#E8552A] hover:underline">Forgot password?</Link></p>
          </>}
        </div>
      </div>
    </div>
  );
}
